import { RefContextLanguage } from '../providers/LanguageProvider';
import { buildPerceptualSurveyMeta, buildSurveyFilename, encodeSurveyFormId } from '../utils/surveyFunctions';
import * as libCon from '../community-hats-js-library/Constants';
import * as locCon from '../LocalConstants';
import CustomText from '../elements/CustomText';
import {
  GenericMultipleSurveysFormStructureButton,
  GenericMultipleSurveysQuestions,
  GenericMultipleSurveysProgressComponentAndExport,
  SurveyComponent,
  RestartSurveyButton,
} from '../elements/SurveyElements';
import {
  getATOField,
  getStoredValue,
  RefStoredMultipleValues,
  RefStoredValue,
  setStoredValue,
} from '../hooks/StoreHooks';

import { useCallback, useEffect, useState } from 'react';
import { filterObjectByKeys, isNullOrUndefined } from '../community-hats-js-library/utils/generalFunctions';
import { showMessageDialogWithLanguageCode } from '../utils/dialogFunctions';

// Biweekly Perceptual Survey Global
export const PERCEPTUAL_SURVEY_CONFIG = {
  encoder: encodeSurveyFormId(libCon.PERCEPTUAL_SURVEY_PREFIX),
  configuration: libCon.LATEST_PERCEPTUAL_SURVEY,
  finalConfigurationKey: locCon.PERCEPTUAL_SURVEY_FINAL_CONFIGURATION,
  folder: libCon.PERCEPTUAL_SURVEY_FOLDER,
  metaBuilder: buildPerceptualSurveyMeta,
  buildFileName: buildSurveyFilename,
  getSurveySerial: () => getATOField(locCon.AT_OBJECT_PARTICIPANT, libCon.ATF_SEWA_ID),
  processConfiguration: async () =>
    // Filter here the questions
    setStoredValue(locCon.PERCEPTUAL_SURVEY_FINAL_CONFIGURATION, libCon.LATEST_PERCEPTUAL_SURVEY),
};

// Biweekly Perceptual Survey for street vendors
export const PERCEPTUAL_STREET_VENDOR_SURVEY_CONFIG = {
  encoder: encodeSurveyFormId(libCon.PERCEPTUAL_STREET_VENDOR_SURVEY_PREFIX),
  configuration: libCon.LATEST_PERCEPTUAL_STREET_VENDOR_SURVEY,
  finalConfigurationKey: locCon.PERCEPTUAL_STREET_VENDOR_SURVEY_FINAL_CONFIGURATION,
  folder: libCon.PERCEPTUAL_STREET_VENDOR_SURVEY_FOLDER,
  metaBuilder: buildPerceptualSurveyMeta,
  buildFileName: buildSurveyFilename,
  getSurveySerial: () => getATOField(locCon.AT_OBJECT_PARTICIPANT, libCon.ATF_SEWA_ID),
  processConfiguration: async () => {
    // Filter here the questions
    if (getATOField(locCon.AT_OBJECT_PARTICIPANT, libCon.ATF_TRADE) !== libCon.ATF_STREET_VENDOR) {
      let formConfig = structuredClone(libCon.LATEST_PERCEPTUAL_STREET_VENDOR_SURVEY);
      formConfig[libCon.SURVEY_STRUCTURE] = [];
      setStoredValue(locCon.PERCEPTUAL_STREET_VENDOR_SURVEY_FINAL_CONFIGURATION, formConfig);
    } else
      setStoredValue(
        locCon.PERCEPTUAL_STREET_VENDOR_SURVEY_FINAL_CONFIGURATION,
        libCon.LATEST_PERCEPTUAL_STREET_VENDOR_SURVEY
      );
  },
};

// Biweekly Perceptual Mental Health Survey
export const PERCEPTUAL_MENTAL_HEALTH_SURVEY_SHORT_CONFIG = {
  encoder: encodeSurveyFormId(libCon.PERCEPTUAL_MENTAL_HEALTH_SURVEY_PREFIX),
  configuration: libCon.LATEST_PERCEPTUAL_MENTAL_HEALTH_SURVEY,
  finalConfigurationKey: locCon.PERCEPTUAL_MENTAL_HEALTH_SURVEY_FINAL_CONFIGURATION,
  folder: libCon.PERCEPTUAL_MENTAL_HEALTH_SURVEY_FOLDER,
  metaBuilder: buildPerceptualSurveyMeta,
  buildFileName: buildSurveyFilename,
  getSurveySerial: () => getATOField(locCon.AT_OBJECT_PARTICIPANT, libCon.ATF_SEWA_ID),
  onCleanUp: (toUpload) => {
    // Activate
    const MIN_SCORE = 3;

    // Maps the score
    // Anxiety
    let anxietySelectedIds = ['gad7_nervous', 'gad7_worry_control'];
    let answerMap = {
      'Not at all': 0,
      'Several days': 1,
      'More than half the days': 2,
      'Nearly every day': 3,
    };
    let anxietyScore = 0;
    if (Object.values(toUpload[libCon.SURVEY_EXPORT_ANSWERS]).length > 0) {
      let answers = Object.values(filterObjectByKeys(toUpload[libCon.SURVEY_EXPORT_ANSWERS], anxietySelectedIds));
      let answerScores = answers.map((answer) => answerMap[answer] || 0);
      anxietyScore = answerScores.reduce((acc, curr) => acc + curr, 0);
    }

    // Depression
    let depressionSelectedIds = ['phq9_interest', 'phq9_depressed'];
    let depressionScore = 0;
    if (Object.values(toUpload[libCon.SURVEY_EXPORT_ANSWERS]).length > 0) {
      let answers = Object.values(filterObjectByKeys(toUpload[libCon.SURVEY_EXPORT_ANSWERS], depressionSelectedIds));
      let answerScores = answers.map((answer) => answerMap[answer] || 0);
      depressionScore = answerScores.reduce((acc, curr) => acc + curr, 0);
    }

    if (anxietyScore >= MIN_SCORE && depressionScore >= MIN_SCORE) {
      showMessageDialogWithLanguageCode(
        'perceptualMentalHealthSurveyHighAnxietyAndDepressionMessage',
        'perceptualMentalHealthSurveyHighScoreTitle'
      );
    } else if (anxietyScore >= MIN_SCORE) {
      showMessageDialogWithLanguageCode(
        'perceptualMentalHealthSurveyHighAnxietyMessage',
        'perceptualMentalHealthSurveyHighScoreTitle'
      );
    } else if (depressionScore >= MIN_SCORE) {
      showMessageDialogWithLanguageCode(
        'perceptualMentalHealthSurveyHighDepressionMessage',
        'perceptualMentalHealthSurveyHighScoreTitle'
      );
    }
  },
  processConfiguration: async () => {
    // Filter here the questions

    let filtered = structuredClone(libCon.LATEST_PERCEPTUAL_MENTAL_HEALTH_SURVEY);

    let selected_ids = [
      'phq9_interest',
      'phq9_depressed',
      'phq9_difficulty',
      'gad7_nervous',
      'gad7_worry_control',
      'gad7_difficulty',
    ];

    // Don't apply to old trades (or trades in mid deployment)
    if (
      [libCon.ATF_HOMEBASE_WORKER, libCon.ATF_BANGLE_FACTORY, libCon.ATF_AGRICULTURE].includes(
        getATOField(locCon.AT_OBJECT_PARTICIPANT, libCon.ATF_TRADE)
      )
    ) {
      selected_ids = [];
    }

    filtered[libCon.SURVEY_STRUCTURE] = filtered[libCon.SURVEY_STRUCTURE].filter((ob) =>
      selected_ids.includes(ob[libCon.SURVEY_QUESTION_ID])
    );

    setStoredValue(locCon.PERCEPTUAL_MENTAL_HEALTH_SURVEY_FINAL_CONFIGURATION, filtered);
  },
};

// Complete Perceptual Mental Health Survey to be administered every 3-4 months
export const PERCEPTUAL_MENTAL_HEALTH_SURVEY_COMPLETE_CONFIG = {
  encoder: encodeSurveyFormId(libCon.PERCEPTUAL_MENTAL_HEALTH_SURVEY_PREFIX),
  configuration: libCon.LATEST_PERCEPTUAL_MENTAL_HEALTH_SURVEY,
  finalConfigurationKey: locCon.PERCEPTUAL_MENTAL_HEALTH_SURVEY_FINAL_CONFIGURATION,
  folder: libCon.PERCEPTUAL_MENTAL_HEALTH_SURVEY_FOLDER,
  metaBuilder: buildPerceptualSurveyMeta,
  buildFileName: buildSurveyFilename,
  getSurveySerial: () => getATOField(locCon.AT_OBJECT_PARTICIPANT, libCon.ATF_SEWA_ID),
  onCleanUp: (toUpload) => {
    // Activate
    const MIN_SCORE = 10;

    // Maps the score
    // Anxiety
    let anxietySkippedIds = ['gad7_difficulty'];
    let answerMap = {
      'Not at all': 0,
      'Several days': 1,
      'More than half the days': 2,
      'Nearly every day': 3,
    };
    let anxietyScore = 0;
    if (Object.values(toUpload[libCon.SURVEY_EXPORT_ANSWERS]).length > 0) {
      let remainingKeys = Object.keys(toUpload[libCon.SURVEY_EXPORT_ANSWERS]).filter(
        (key) => key.includes('gad7_') && !anxietySkippedIds.includes(key)
      );
      console.log(remainingKeys);
      let answers = Object.values(filterObjectByKeys(toUpload[libCon.SURVEY_EXPORT_ANSWERS], remainingKeys));
      let answerScores = answers.map((answer) => answerMap[answer] || 0);
      anxietyScore = answerScores.reduce((acc, curr) => acc + curr, 0);
    }

    // Depression
    let depressionSkippedIds = ['phq9_difficulty'];
    let depressionScore = 0;
    if (Object.values(toUpload[libCon.SURVEY_EXPORT_ANSWERS]).length > 0) {
      let remainingKeys = Object.keys(toUpload[libCon.SURVEY_EXPORT_ANSWERS]).filter(
        (key) => key.includes('phq9_') && !depressionSkippedIds.includes(key)
      );
      console.log(remainingKeys);
      let answers = Object.values(filterObjectByKeys(toUpload[libCon.SURVEY_EXPORT_ANSWERS], remainingKeys));
      let answerScores = answers.map((answer) => answerMap[answer] || 0);
      depressionScore = answerScores.reduce((acc, curr) => acc + curr, 0);
    }

    if (anxietyScore >= MIN_SCORE && depressionScore >= MIN_SCORE) {
      showMessageDialogWithLanguageCode(
        'perceptualMentalHealthSurveyHighAnxietyAndDepressionMessage',
        'perceptualMentalHealthSurveyHighScoreTitle'
      );
    } else if (anxietyScore >= MIN_SCORE) {
      showMessageDialogWithLanguageCode(
        'perceptualMentalHealthSurveyHighAnxietyMessage',
        'perceptualMentalHealthSurveyHighScoreTitle'
      );
    } else if (depressionScore >= MIN_SCORE) {
      showMessageDialogWithLanguageCode(
        'perceptualMentalHealthSurveyHighDepressionMessage',
        'perceptualMentalHealthSurveyHighScoreTitle'
      );
    }
  },
  processConfiguration: async () => {
    // Filter here the questions
    setStoredValue(
      locCon.PERCEPTUAL_MENTAL_HEALTH_SURVEY_FINAL_CONFIGURATION,
      libCon.LATEST_PERCEPTUAL_MENTAL_HEALTH_SURVEY
    );
  },
};

// Womenth Health Survey that should be administered every 3-4 months
export const PERCEPTUAL_WOMENS_HEALTH_SURVEY_CONFIG = {
  encoder: encodeSurveyFormId(libCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_PREFIX),
  configuration: libCon.LATEST_PERCEPTUAL_WOMENS_HEALTH_SURVEY,
  finalConfigurationKey: locCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_FINAL_CONFIGURATION,
  folder: libCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_FOLDER,
  metaBuilder: buildPerceptualSurveyMeta,
  buildFileName: buildSurveyFilename,
  getSurveySerial: () => getATOField(locCon.AT_OBJECT_PARTICIPANT, libCon.ATF_SEWA_ID),
  processConfiguration: async () => {
    // Loads the spaguetti questions

    let encoder = encodeSurveyFormId(libCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_PREFIX);
    const reproductiveStageAnswer = getStoredValue(encoder(libCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_REPRODUCTIVE_STAGE));
    const cycleRegularAnswer = getStoredValue(encoder(libCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_CYCLE_REGULAR));

    let filtered = structuredClone(libCon.LATEST_PERCEPTUAL_WOMENS_HEALTH_SURVEY);
    let selected_ids = [];

    switch (reproductiveStageAnswer) {
      case libCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_REPRODUCTIVE_STAGE_PREGNANT:
        selected_ids = [`pregnancy_due_date`];
        break;
      case libCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_REPRODUCTIVE_STAGE_LACTATING:
        selected_ids = [`pregnancy_end_date`];
        break;

      case libCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_REPRODUCTIVE_STAGE_MENSTRUATING:
        selected_ids = [`cycle_change`, `work_through_cycle`];
        let responses = ['No', 'Don’t know'];
        if (responses.includes(cycleRegularAnswer[libCon.SURVEY_BINARY_FOLLOWUP_FIRST_QUESTION]))
          selected_ids = [
            ...selected_ids,
            `hf_ns_present`,
            `hf_ns_frequency_last_week`,
            `hf_ns_problem_rating`,
            `hf_ns_distress_rating`,
            `hf_ns_interference_rating`,
          ];

        break;
      case libCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_REPRODUCTIVE_STAGE_NO_CYCLE:
        selected_ids = [
          `last_cycle_timing`,
          `hf_ns_present`,
          `hf_ns_frequency_last_week`,
          `hf_ns_problem_rating`,
          `hf_ns_distress_rating`,
          `hf_ns_interference_rating`,
        ];
        break;

      default:
        break;
    }
    filtered[libCon.SURVEY_STRUCTURE] = filtered[libCon.SURVEY_STRUCTURE].filter((ob) =>
      selected_ids.includes(ob[libCon.SURVEY_QUESTION_ID])
    );

    setStoredValue(locCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_FINAL_CONFIGURATION, filtered);
  },
};

export function RecurentSurveys() {
  // TOGGLE RECURRENT SURVEYS
  const includedConfigurations = [
    PERCEPTUAL_SURVEY_CONFIG,
    PERCEPTUAL_MENTAL_HEALTH_SURVEY_SHORT_CONFIG,
    PERCEPTUAL_STREET_VENDOR_SURVEY_CONFIG,
  ];

  const i18n = RefContextLanguage();
  const instructionsText = i18n.t('perceptualFormInstructions');
  const title = i18n.t('perceptualSurvey');

  return (
    <GeneralRecurentSurveys
      includedConfigurations={includedConfigurations}
      instructionsText={instructionsText}
      title={title}
    />
  );
}

export function WomensAndMentalHealthSurvey() {
  // Genweral Survey Configuration
  const i18n = RefContextLanguage();
  const instructionsText = i18n.t('womensAndMentalHealthFormInstructions');
  const title = i18n.t('womensAndMentalHealthSurvey');

  // TOGGLE RECURRENT SURVEYS
  const includedConfigurations = [
    PERCEPTUAL_WOMENS_HEALTH_SURVEY_CONFIG,
    PERCEPTUAL_MENTAL_HEALTH_SURVEY_COMPLETE_CONFIG,
  ];

  const configurationStatusId = locCon.RECURENT_SURVEY_CONFIGURATION_STATUS;

  const sendingStatusId = locCon.RS_CONNECTION_SENDING_ANSWERS_STATUS;

  const encodersArray = includedConfigurations.map((config) => config.encoder);
  const surveyConfigurationsArray = includedConfigurations.map((config) => config.configuration);
  const folderNamesArray = includedConfigurations.map((config) => config.folder);
  const metaBuildersArray = includedConfigurations.map((config) => config.metaBuilder);
  const buildFilenamesArray = includedConfigurations.map((config) => config.buildFileName);
  const getSurveySerialsArray = includedConfigurations.map((config) => config.getSurveySerial);
  const processConfigurationArray = includedConfigurations.map((config) => config.processConfiguration);
  const onCleanUpArray = includedConfigurations.map((config) => config.onCleanUp);

  const finalConfigurationsObject = RefStoredMultipleValues(
    includedConfigurations.map((config) => config.finalConfigurationKey)
  );

  // Intermedeate co figuration

  const encoder = useCallback((id) => {
    let currentEncoder = encodeSurveyFormId(libCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_PREFIX);
    return currentEncoder(id);
  }, []);

  const restartProcess = (showMessage = true) => {
    setShowForm(false);
  };

  const [showForm, setShowForm] = useState(false);

  const reproductiveStageConfiguration = libCon.LATEST_PERCEPTUAL_WOMENS_HEALTH_SURVEY[libCon.SURVEY_STRUCTURE].filter(
    (q) => q[libCon.SURVEY_QUESTION_ID] === libCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_REPRODUCTIVE_STAGE
  )[0];
  const reproductiveStageAnswer = RefStoredValue(encoder(libCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_REPRODUCTIVE_STAGE));

  const cycleRegularConfiguration = libCon.LATEST_PERCEPTUAL_WOMENS_HEALTH_SURVEY[libCon.SURVEY_STRUCTURE].filter(
    (q) => q[libCon.SURVEY_QUESTION_ID] === libCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_CYCLE_REGULAR
  )[0];
  const cycleRegularStageAnswer = RefStoredValue(encoder(libCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_CYCLE_REGULAR));
  const [needsSecondAnswer, setNeedsSecondAnswer] = useState(false);

  useEffect(() => {
    if (!isNullOrUndefined(reproductiveStageAnswer)) {
      if (reproductiveStageAnswer === `Currently have a cycle/menstruating`) setNeedsSecondAnswer(true);
      else setNeedsSecondAnswer(false);

      setStoredValue(libCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_CYCLE_REGULAR, null);
    }
  }, [reproductiveStageAnswer]);

  const attachAnswers = (toUpload) => {
    if (toUpload[libCon.SURVEY_METADATA][libCon.SURVEY_NAME] !== `Women's Health Survey`) return toUpload;

    if (!(libCon.SURVEY_EXPORT_ANSWERS in toUpload)) toUpload[libCon.SURVEY_EXPORT_ANSWERS] = {};

    toUpload[libCon.SURVEY_EXPORT_ANSWERS] = {
      ...toUpload[libCon.SURVEY_EXPORT_ANSWERS],
      [libCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_REPRODUCTIVE_STAGE]: reproductiveStageAnswer,
    };

    if (!isNullOrUndefined(cycleRegularStageAnswer)) {
      toUpload[libCon.SURVEY_EXPORT_ANSWERS][libCon.PERCEPTUAL_WOMENS_HEALTH_SURVEY_CYCLE_REGULAR] =
        cycleRegularStageAnswer;
    }

    return toUpload;
  };

  return (
    <div className="verticalSection" style={{ marginBottom: 25 }}>
      <CustomText type={locCon.SECTION_TITLE_TEXT} style={{ marginBottom: '1.5vh' }}>
        {title}
      </CustomText>

      <div style={{ marginTop: '2vh' }}></div>

      <RestartSurveyButton
        encodersArray={encodersArray}
        surveyConfigurationsArray={surveyConfigurationsArray}
        onCleanUp={restartProcess}
      />

      <SurveyComponent
        configuration={reproductiveStageConfiguration}
        titlePrefix={``}
        encoder={encoder}
        section={undefined}
      />
      {!isNullOrUndefined(reproductiveStageAnswer) && needsSecondAnswer ? (
        <SurveyComponent
          configuration={cycleRegularConfiguration}
          titlePrefix={``}
          encoder={encoder}
          section={undefined}
        />
      ) : (
        <div></div>
      )}
      {!isNullOrUndefined(reproductiveStageAnswer) &&
      (!isNullOrUndefined(cycleRegularStageAnswer) || !needsSecondAnswer) ? (
        <>
          <GenericMultipleSurveysFormStructureButton
            surveyConfigurationsArray={surveyConfigurationsArray}
            configurationStatusId={configurationStatusId}
            sendingStatusId={sendingStatusId}
            processConfigurationArray={processConfigurationArray}
            onCleanUp={() => setShowForm(true)}
            showVersions={false}
          />

          {showForm ? (
            <>
              <GenericMultipleSurveysQuestions
                encodersArray={encodersArray}
                surveyConfigurationsArray={Object.values(finalConfigurationsObject)}
                configurationStatusId={configurationStatusId}
                instructionsText={instructionsText}
                includeRestartButton={false}
              />

              <GenericMultipleSurveysProgressComponentAndExport
                surveyConfigurationsArray={Object.values(finalConfigurationsObject)}
                sendingStatusId={sendingStatusId}
                configurationStatusId={configurationStatusId}
                buildFilenamesArray={buildFilenamesArray}
                folderNamesArray={folderNamesArray}
                encodersArray={encodersArray}
                metaBuildersArray={metaBuildersArray}
                getSurveySerialsArray={getSurveySerialsArray}
                postProcessSurvey={attachAnswers}
                onCleanUpArray={onCleanUpArray}
              />
            </>
          ) : (
            <></>
          )}
        </>
      ) : (
        <div></div>
      )}
    </div>
  );
}

export function GeneralRecurentSurveys({ includedConfigurations, instructionsText, title }) {
  const configurationStatusId = locCon.RECURENT_SURVEY_CONFIGURATION_STATUS;

  const sendingStatusId = locCon.RS_CONNECTION_SENDING_ANSWERS_STATUS;

  const encodersArray = includedConfigurations.map((config) => config.encoder);
  const surveyConfigurationsArray = includedConfigurations.map((config) => config.configuration);
  const folderNamesArray = includedConfigurations.map((config) => config.folder);
  const metaBuildersArray = includedConfigurations.map((config) => config.metaBuilder);
  const buildFilenamesArray = includedConfigurations.map((config) => config.buildFileName);
  const getSurveySerialsArray = includedConfigurations.map((config) => config.getSurveySerial);
  const processConfigurationArray = includedConfigurations.map((config) => config.processConfiguration);
  const onCleanUpArray = includedConfigurations.map((config) => config.onCleanUp);

  const finalConfigurationsObject = RefStoredMultipleValues(
    includedConfigurations.map((config) => config.finalConfigurationKey)
  );

  return (
    <div className="verticalSection" style={{ marginBottom: 25 }}>
      <CustomText type={locCon.SECTION_TITLE_TEXT} style={{ marginBottom: '1.5vh' }}>
        {title}
      </CustomText>

      <GenericMultipleSurveysFormStructureButton
        surveyConfigurationsArray={surveyConfigurationsArray}
        configurationStatusId={configurationStatusId}
        sendingStatusId={sendingStatusId}
        processConfigurationArray={processConfigurationArray}
      />

      <GenericMultipleSurveysQuestions
        encodersArray={encodersArray}
        surveyConfigurationsArray={Object.values(finalConfigurationsObject)}
        configurationStatusId={configurationStatusId}
        instructionsText={instructionsText}
      />

      <GenericMultipleSurveysProgressComponentAndExport
        surveyConfigurationsArray={Object.values(finalConfigurationsObject)}
        sendingStatusId={sendingStatusId}
        configurationStatusId={configurationStatusId}
        buildFilenamesArray={buildFilenamesArray}
        folderNamesArray={folderNamesArray}
        encodersArray={encodersArray}
        metaBuildersArray={metaBuildersArray}
        getSurveySerialsArray={getSurveySerialsArray}
        onCleanUpArray={onCleanUpArray}
      />
    </div>
  );
}

export default RecurentSurveys;
